'use client';

import { getShortListVillas } from '@/actions/villages';
import { useTranslation } from '@/contexts/dictionaryContext';
import { useLocale } from '@/hooks/useLocale';
import useLocalStorage from '@/hooks/useLocalStorage';
import { IExperience } from '@/types/Experience';
import { IVilla } from '@/types/Villa';
import React, { useCallback, useEffect, useState } from 'react';
import { ShortListButton } from './ShortListButton';
import { ShortListExperienceItem, ShortListVillaItem } from './ShortListItem';

type IShortListModalContentProps = {
  simpleButton?: boolean;
};

const ShortListModalContent: React.FC<IShortListModalContentProps> = ({
  simpleButton = false,
}) => {
  const lang = useLocale();
  const { modals, enquiryForm, sendToFriendForm, countries, datePicker } =
    useTranslation();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [shortListVillas, setShortListVillas] = useState<IVilla[]>([]);
  const [villaShortList, setVillaShortList] = useLocalStorage(
    'villas',
    [] as IVilla[],
  );
  const [experienceShortList, setExperienceShortList] = useLocalStorage(
    'experience',
    [] as IExperience[],
  );

  const handleRemoveShortItemFromList = useCallback(
    (itemType: string, id: number | string) => {
      if (typeof window !== 'undefined') {
        if (itemType === 'villa') {
          const updatedFavourites = villaShortList.filter(
            (item) => item.title !== id,
          );
          setVillaShortList(updatedFavourites);
        }
        if (itemType === 'experience') {
          const updatedFavourites = experienceShortList.filter(
            (item) => item.id !== id,
          );
          setExperienceShortList(updatedFavourites);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [villaShortList, experienceShortList],
  );

  useEffect(() => {
    const ids = villaShortList.map((villa) => villa.id);
    let cancelled = false;

    async function loadShortListVillas() {
      if (ids.length === 0) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const villas = await getShortListVillas(ids);

      if (cancelled) return;

      const villasWithStoredCurrency = (villas ?? []).map((villa) => {
        const storedVilla = villaShortList.find((item) => item.id === villa.id);

        if (!storedVilla) return villa;

        return {
          ...villa,
          prices: {
            ...villa.prices,
            currency: storedVilla.prices.currency,
          },
        };
      });

      setShortListVillas(villasWithStoredCurrency);
      setIsLoading(false);
    }

    loadShortListVillas();

    return () => {
      cancelled = true;
    };
  }, [villaShortList]);

  return (
    <ShortListButton
      simpleButton={simpleButton}
      shortListLength={villaShortList.length + experienceShortList.length}
      lang={lang}
      hasVillas={villaShortList.length > 0}
      hasExperience={experienceShortList.length > 0}
      enquiryFormDict={enquiryForm}
      sendToFriendFormDict={sendToFriendForm}
      countriesDict={countries}
      datePickerDict={datePicker}
      modalsDict={modals}
      dict={modals.shortListModal}
      modalIsOpen={modalIsOpen}
      setModalIsOpen={setModalIsOpen}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      villasShortListModalContent={
        <>
          {villaShortList.length > 0 ? (
            shortListVillas.map((villa) => (
              <ShortListVillaItem
                lang={lang}
                itemRemove={handleRemoveShortItemFromList}
                dict={modals.shortListModal}
                key={villa.id}
                villa={villa}
                setModalIsOpen={setModalIsOpen}
              />
            ))
          ) : (
            <h2 className="w-full text-center font-title text-[22px] font-semibold mt-16 text-white">
              {modals.shortListModal.emptyVillas}
            </h2>
          )}
        </>
      }
      experiencesShortListModalContent={
        <>
          {experienceShortList.length > 0 &&
            experienceShortList.map((experience, index) => (
              <ShortListExperienceItem
                lang={lang}
                itemRemove={handleRemoveShortItemFromList}
                dict={modals.shortListModal}
                key={index}
                experience={experience}
                setModalIsOpen={setModalIsOpen}
              />
            ))}
        </>
      }
    />
  );
};

export default ShortListModalContent;
